import { useLanguage } from '../i18n/LanguageProvider';
import React from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Notification, NotificationService } from '../services/NotificationService';
import { BsFillXCircleFill } from 'react-icons/bs';
import './NotificationToast.css';

interface ToastItemProps {
  notification: Notification;
  index: number;
  onClose: () => void;
  onClick: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ notification, index, onClose, onClick }) => {
  const i18n = useLanguage();
  const [isLeaving, setIsLeaving] = React.useState(false);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLeaving(true);
    setTimeout(onClose, 300);
  };

  const handleClick = () => {
    setIsLeaving(true);
    setTimeout(onClick, 300);
  };

  return (
    <div 
      className={`notification-toast ${isLeaving ? 'leaving' : ''}`}
      style={{ 
        '--index': index,
        '--accent-color': notification.color 
      } as React.CSSProperties}
      onClick={handleClick}
    >
      <div className="toast-accent" style={{ backgroundColor: notification.color }} />
      <div className="toast-icon">{notification.icon}</div>
      <div className="toast-content">
        <div className="toast-title">{i18n.owned(notification.title)}</div>
        <div className="toast-message">{i18n.owned(notification.message)}</div>
        <div className="toast-time">
          {Date.now() - notification.createdAt < 7 * 86400000
                    ? i18n.owned(NotificationService.formatTimestamp(notification.createdAt))
                    : new Date(notification.createdAt).toLocaleDateString(i18n.locale)}
        </div>
      </div>
      <div 
        className="toast-close" 
        onClick={handleClose}
        aria-label={i18n.t("Close notification")}
      >
        <BsFillXCircleFill />
      </div>
    </div>
  );
};

export const NotificationToast: React.FC = () => {
  const { activeToasts, clearToast, markAsRead } = useNotifications();

  const handleToastClick = async (notification: Notification) => {
    // Mark as read when clicked
    await markAsRead(notification.id, notification.createdAt);
    clearToast(notification.id);
    
    // Open action URL if present
    if (notification.actionUrl) {
      // Check if it's an external URL or internal path
      if (notification.actionUrl.startsWith('http')) {
        window.open(notification.actionUrl, '_blank');
      } else {
        window.location.href = notification.actionUrl;
      }
    }
  };

  if (activeToasts.length === 0) {
    return null;
  }

  return (
    <div className="notification-toast-container">
      {activeToasts.map((notification, index) => (
        <ToastItem
          key={notification.id}
          notification={notification}
          index={index}
          onClose={() => clearToast(notification.id)}
          onClick={() => handleToastClick(notification)}
        />
      ))}
    </div>
  );
};

export default NotificationToast;

